/**
 * app-config-form-mappers.ts — apps 域「契约 DTO ↔ 表单显示值」纯函数映射
 *
 * 详情页 / 新建向导共用：
 *   - TASK_CENTER_REWARDS_DEFAULT：任务中心 BONUS 奖励默认值（dev-docs ADM-APP-S010 闭集逐项一致）
 *   - parseTaskCenterRewards：DTO task_center_rewards（可能为历史宽 jsonb）→ 契约形状，解析失败回默认
 *   - TASK_CENTER_TASK_DISPLAY_LABELS：8 个 slug 的中文展示名（ADM-APP-S016）
 *   - parseTaskCenterTaskDisplay：DTO task_center_task_display（宽 jsonb）→ 契约形状，解析失败回默认
 *   - ratioToPct / pctToRatio：third_party_bonus_ratio decimal(5,4) 字符串 ↔ 表单百分比字符串
 *   - normalizeAppCodeDraft / normalizeAppCodeInput / appCodeInputIssue / appCodeServerIssue：包体代号
 *     （ADM-APP-S019）失焦归一、提交值归一、前端预校验与服务端错误 → 行内提示
 */

import {
  AppCodeSchema,
  DEFAULT_TASK_CENTER_TASK_DISPLAY,
  type TaskCenterDisplaySlug,
  type TaskCenterRewards,
  TaskCenterRewardsSchema,
  type TaskCenterTaskDisplayEntry,
  TaskCenterTaskDisplaySchema,
} from '@novel-admin/shared';
import { AdminApiError } from '~/api/errors';

/** ADM-APP-S010 系统预置任务奖励默认值（应用评分/开启通知/账号登录/绑定邮箱 20 · 每日首充 30 · 签到/阅读/解锁阶梯） */
export const TASK_CENTER_REWARDS_DEFAULT: TaskCenterRewards = {
  fixed: {
    app_review: 20,
    enable_notification: 20,
    account_login: 20,
    bind_email: 20,
    daily_first_recharge: 30,
  },
  tiered: {
    daily_signin: [10, 10, 15, 15, 20, 20, 20],
    reading_time: [
      { minutes: 10, reward: 10 },
      { minutes: 30, reward: 20 },
      { minutes: 60, reward: 30 },
    ],
    unlock_chapter: [
      { chapters: 10, reward: 20 },
      { chapters: 20, reward: 35 },
      { chapters: 30, reward: 50 },
    ],
  },
};

/** DTO task_center_rewards → 契约 TaskCenterRewards；历史宽 jsonb 解析失败时回 S010 默认（不渲染坏数据） */
export function parseTaskCenterRewards(value: unknown): TaskCenterRewards {
  const parsed = TaskCenterRewardsSchema.safeParse(value);
  return parsed.success ? parsed.data : structuredClone(TASK_CENTER_REWARDS_DEFAULT);
}

/** '0.3500'（decimal(5,4) 比例）→ '35'（表单百分比）；取整到百分位避免浮点尾差 */
export function ratioToPct(ratio: string): string {
  const n = Number.parseFloat(ratio);
  if (!Number.isFinite(n)) return '0';
  return String(Math.round(n * 10000) / 100);
}

/** '35'（表单百分比）→ '0.3500'（契约 BonusRatioSchema decimal ≤4 位小数） */
export function pctToRatio(pct: string): string {
  const n = Number.parseFloat(pct);
  if (!Number.isFinite(n)) return '0.0000';
  return (n / 100).toFixed(4);
}

/**
 * ADM-APP-S016 —— 8 个普通任务 slug 的中文展示名。
 *
 * 顺序**不由本表承载**：展示顺序是运营配的数组顺序（契约 §1.2「元素顺序 = C 端展示顺序」），
 * 这里只做 slug → 文案的查表。写成 `Record<TaskCenterDisplaySlug, string>` 而不是数组，
 * 让「slug 全集将来新增一项而这里漏配文案」变成**编译错误**，而不是在界面上渲染出一个裸 slug。
 */
export const TASK_CENTER_TASK_DISPLAY_LABELS: Record<TaskCenterDisplaySlug, string> = {
  watch_rewarded_ad: '观看激励广告',
  app_review: '应用评分',
  enable_notification: '开启通知',
  account_login: '账号登录',
  bind_email: '绑定邮箱',
  daily_first_recharge: '每日首充',
  reading_time: '阅读时长',
  unlock_chapter: '付费章节解锁',
};

/**
 * DTO `task_center_task_display`（宽 jsonb，可能被手工 SQL 改坏）→ 契约形状。
 *
 * 解析失败回**契约默认值**（8 项全开 + 契约顺序），与 `parseTaskCenterRewards` 同口径：
 * 不把坏数据渲染出来给运营编辑 —— 那会让他一保存就把坏数据原样写回去。
 *
 * ⚠️ 这里回默认值是**渲染侧**的选择，与 C 端运行时解析器的 fail-closed（异常输入按关闭处理）
 * 方向相反，两者不冲突：C 端要防的是「配置坏了却把任务放出去」，这里要防的是
 * 「拿坏数据当运营的既有配置展示」。真正的写侧硬校验在 `TaskCenterTaskDisplaySchema`。
 */
export function parseTaskCenterTaskDisplay(value: unknown): TaskCenterTaskDisplayEntry[] {
  const parsed = TaskCenterTaskDisplaySchema.safeParse(value);
  return parsed.success ? parsed.data : [...DEFAULT_TASK_CENTER_TASK_DISPLAY];
}

/** ADM-APP-S019 包体代号的格式说明（判据 = shared `AppCodeSchema` = C 端 CHECK `apps_app_code_shape`） */
export const APP_CODE_FORMAT_HINT = '大写字母与数字 2–12 位';

/**
 * 输入框原值 → 归一后的显示值：去前后空白、转大写。只在失焦 / 提交时用 —— 每次按键就改写受控值会把
 * 光标跳到末尾，并打断输入法（拼音等）的组字。输入中的大写观感交给输入框的 `text-transform`。
 */
export function normalizeAppCodeDraft(raw: string): string {
  return raw.trim().toUpperCase();
}

/**
 * 输入框值 → 提交值：同 `normalizeAppCodeDraft`，空 = 未配置（`null`）。
 * 归一只在前端做：服务端按契约对不合 `^[A-Z0-9]{2,12}$` 的值直接 `VALIDATION_FAILED`、不替运营改写。
 */
export function normalizeAppCodeInput(raw: string): string | null {
  const value = normalizeAppCodeDraft(raw);
  return value === '' ? null : value;
}

/** 前端预校验（与服务端同一 schema）：合法或留空返 `null`，否则返可读中文提示 */
export function appCodeInputIssue(raw: string): string | null {
  const value = normalizeAppCodeInput(raw);
  if (value === null || AppCodeSchema.safeParse(value).success) return null;
  return `包体代号须为${APP_CODE_FORMAT_HINT}（如 NVPLI），或留空`;
}

/**
 * 服务端错误 → 包体代号行内提示；与本字段无关返 `null`（调用方照常 toast）。
 * 冲突判据是 `details.conflict_field`，不是 message 文案 —— 文案会随本地化改，字段名是契约。
 */
export function appCodeServerIssue(err: unknown): string | null {
  if (!(err instanceof AdminApiError)) return null;
  if (err.code === 'RESOURCE_CONFLICT' && err.details?.conflict_field === 'app_code') {
    return '包体代号已被其他 App 占用（已删除的 App 也继续占用），请换一个';
  }
  if (err.code === 'VALIDATION_FAILED' && err.errors?.some((e) => e.field === 'app_code')) {
    return `包体代号须为${APP_CODE_FORMAT_HINT}（如 NVPLI），或留空`;
  }
  return null;
}
